import { create } from 'zustand';
import {
  ALL_FILTER_VALUE,
  BreakdownDimension,
  BreakdownDimensionType,
  ChannelType,
  MapPointFilter,
  MapPointFilterType,
  MapTimeView,
  MapTimeViewType,
  PenetrationV2Filters,
  Period,
  SignalTileKeyType,
  SolutionType,
} from '../services/penetrationReportV2/types';
import { DEFAULT_AS_OF_PERIOD, DEFAULT_COMPARE_PERIOD } from '../services/penetrationReportV2/seedData';

const DEFAULT_FILTERS: PenetrationV2Filters = {
  brand: ALL_FILTER_VALUE,
  channel: ALL_FILTER_VALUE,
  solution: ALL_FILTER_VALUE,
};

export interface PenetrationReportV2State {
  filters: PenetrationV2Filters;
  selectedSignalTile: SignalTileKeyType | null;
  comparisonEnabled: boolean;
  asOfPeriod: Period;
  comparePeriod: Period;
  breakdownTab: BreakdownDimensionType;
  mapTimeView: MapTimeViewType;
  mapPointFilter: MapPointFilterType;
  /** Trend chart's rolling window length, in months (see TREND_RANGE_OPTIONS). */
  trendRangeMonths: number;

  setBrandFilter: (brand: string) => void;
  setChannelFilter: (channel: ChannelType | typeof ALL_FILTER_VALUE) => void;
  setSolutionFilter: (solution: SolutionType | typeof ALL_FILTER_VALUE) => void;
  resetFilters: () => void;
  toggleSignalTile: (key: SignalTileKeyType) => void;
  setComparisonEnabled: (enabled: boolean) => void;
  setBreakdownTab: (tab: BreakdownDimensionType) => void;
  setMapTimeView: (view: MapTimeViewType) => void;
  setMapPointFilter: (filter: MapPointFilterType) => void;
  setAsOfPeriod: (period: Period) => void;
  setComparePeriod: (period: Period) => void;
  setTrendRangeMonths: (months: number) => void;
}

const usePenetrationReportV2Store = create<PenetrationReportV2State>()((set) => ({
  filters: DEFAULT_FILTERS,
  selectedSignalTile: null,
  comparisonEnabled: true,
  asOfPeriod: DEFAULT_AS_OF_PERIOD,
  comparePeriod: DEFAULT_COMPARE_PERIOD,
  breakdownTab: BreakdownDimension.BRAND,
  mapTimeView: MapTimeView.AS_OF,
  mapPointFilter: MapPointFilter.ALL,
  trendRangeMonths: 12,

  setBrandFilter: (brand) => set((s) => ({ filters: { ...s.filters, brand } })),
  setChannelFilter: (channel) => set((s) => ({ filters: { ...s.filters, channel } })),
  setSolutionFilter: (solution) => set((s) => ({ filters: { ...s.filters, solution } })),
  resetFilters: () => set({ filters: DEFAULT_FILTERS }),
  toggleSignalTile: (key) => set((s) => ({ selectedSignalTile: s.selectedSignalTile === key ? null : key })),
  setComparisonEnabled: (comparisonEnabled) => set({ comparisonEnabled }),
  setBreakdownTab: (breakdownTab) => set({ breakdownTab }),
  setMapTimeView: (mapTimeView) => set({ mapTimeView }),
  setMapPointFilter: (mapPointFilter) => set({ mapPointFilter }),
  setAsOfPeriod: (asOfPeriod) => set({ asOfPeriod }),
  setComparePeriod: (comparePeriod) => set({ comparePeriod }),
  setTrendRangeMonths: (trendRangeMonths) => set({ trendRangeMonths }),
}));

export default usePenetrationReportV2Store;
